'use client';
import { cva, type VariantProps } from 'class-variance-authority';
import { twMerge } from 'tailwind-merge';
import { ButtonHTMLAttributes, ReactNode, CSSProperties } from 'react';

const buttonClasses = cva(
    'inline-flex items-center justify-center font-medium transition-all duration-200 focus:outline-none disabled:opacity-50 disabled:cursor-not-allowed hover:opacity-90 active:scale-95',
    {
        variants: {
            variant: {
                primary: 'bg-primary-brand text-text-on-dark hover:bg-primary-brand-dark',
                secondary: 'bg-background-secondary text-text-primary hover:bg-secondary-light',
                outline: 'border-2 border-primary-brand text-primary-brand bg-transparent hover:bg-primary-brand hover:text-text-on-dark',
                ghost: 'bg-transparent text-text-primary hover:bg-background-secondary',
            },
            size: {
                small: 'text-sm px-3 py-1.5 sm:px-4 sm:py-2',
                medium: 'text-base px-4 py-2 sm:px-5 sm:py-2.5 md:px-6 md:py-3',
                large: 'text-lg px-6 py-3 sm:px-8 sm:py-4 md:px-10 md:py-5',
            },
        },
        defaultVariants: {
            variant: 'primary',
            size: 'medium',
        },
    }
)

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement>, VariantProps<typeof buttonClasses> {
    // Required parameters with defaults
    text?: string;
    text_font_size?: string;
    text_font_family?: string;
    text_font_weight?: string;
    text_line_height?: string;
    text_text_align?: string;
    text_color?: string;
    fill_background_color?: string;
    border_border_radius?: string;

    // Optional parameters
    border_border?: string;
    layout_gap?: string;
    layout_width?: string;
    padding?: string;
    margin?: string;
    position?: string;

    // Standard React props
    children?: ReactNode;
}

const Button = ({
    // Required parameters with defaults
    text = "Shop Now",
    text_font_size = "text-md",
    text_font_family = "Inter",
    text_font_weight = "font-medium",
    text_line_height = "leading-md",
    text_text_align = "left",
    text_color = "text-text-on-dark",
    fill_background_color = "bg-primary-brand",
    border_border_radius = "rounded-xs",

    // Optional parameters (no defaults)
    border_border,
    layout_gap,
    layout_width,
    padding,
    margin,
    position,

    // Standard React props
    variant,
    size,
    disabled = false,
    className,
    children,
    onClick,
    type = 'button',
    ...props
}: ButtonProps) => {
    // Safe validation for optional parameters
    const hasValidBorder = border_border && typeof border_border === 'string' && border_border.trim() !== ''
    const hasValidGap = layout_gap && typeof layout_gap === 'string' && layout_gap.trim() !== ''
    const hasValidWidth = layout_width && typeof layout_width === 'string' && layout_width.trim() !== ''
    const hasValidPadding = padding && typeof padding === 'string' && padding.trim() !== ''
    const hasValidMargin = margin && typeof margin === 'string' && margin.trim() !== ''
    const hasValidPosition = position && typeof position === 'string' && position.trim() !== ''

    // Parse optional border
    let borderClasses = ''
    if (hasValidBorder) {
        const borderParts = border_border.split(' ')
        if (borderParts.length >= 3) {
            const width = borderParts[0]
            const style = borderParts[1]
            const color = borderParts[2]
            borderClasses = `border-[${width}] border-${style} border-[${color}]`
        }
    }

    const optionalClasses = [
        hasValidGap ? `gap-[${layout_gap}]` : '',
        hasValidWidth ? (layout_width === 'full' ? 'w-full' : `w-[${layout_width}]`) : '',
        hasValidPadding ? `p-[${padding}]` : '',
        hasValidMargin ? `m-[${margin}]` : '',
        hasValidPosition ? position : '',
        borderClasses,
    ].filter(Boolean).join(' ')

    // Build custom styles for non-Tailwind properties
    const customStyles: CSSProperties = {
        ...(text_font_family && !text_font_family.startsWith('font-') && { fontFamily: text_font_family }),
    }

    // Build Tailwind classes for styling (only when not using variant)
    const styleClasses = !variant ? [
        text_font_size,
        text_font_weight,
        text_line_height,
        `text-${text_text_align}`,
        text_color,
        fill_background_color,
        border_border_radius,
    ].filter(Boolean).join(' ') : ''

    const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
        if (disabled) {
            event.preventDefault()
            return
        }

        if (typeof onClick === 'function') {
            onClick(event)
        }
    }

    return (
        <button
            type={type}
            disabled={disabled}
            onClick={handleClick}
            style={customStyles}
            className={twMerge(
                buttonClasses({ variant, size }),
                styleClasses,
                optionalClasses,
                className
            )}
            aria-disabled={disabled}
            {...props}
        >
            {children || text}
        </button>
    )
}

export default Button